/**
 * Lazy loader for CesiumJS and ol-cesium.
 *
 * CesiumJS is imported at runtime rather than bundled. ol-cesium reaches it
 * exclusively through the `window.Cesium` global — every `cesium` import in its
 * compiled output imports no bindings at all — so bundling would add several
 * megabytes to the build for a global that still has to be assigned by hand.
 * The prebuilt ES module is copied to `dist/cesium/<version>/` by CopyPlugin
 * (see webpack.config.base.js), the bundler is told to resolve the `cesium`
 * specifier away entirely, and the import below carries `webpackIgnore` so the
 * browser fetches it directly.
 *
 * Nothing here runs until the user first turns 3D on, which is the point: the
 * payload is roughly 6 MB and most sessions never ask for it.
 */

type CesiumNamespace = Record<string, unknown>

declare global {
  interface Window {
    Cesium?: CesiumNamespace
    CESIUM_BASE_URL?: string
  }
}

/** Imports a module by URL at runtime. */
export type ModuleImporter = (url: string) => Promise<Record<string, unknown>>

// webpackIgnore keeps this as a native dynamic import, so the browser fetches
// the prebuilt Cesium from CESIUM_BASE_URL instead of webpack trying to resolve
// and bundle it.
const defaultImporter: ModuleImporter = (url) =>
  import(/* webpackIgnore: true */ url)

/**
 * The OLCesium class, imported from its own module rather than from the olcs
 * barrel.
 *
 * The barrel pulls in `contrib/Manager`, which in 2.23.1 imports `'../core'`
 * with no file extension — invalid in a strict-ESM package, so webpack refuses
 * to resolve it. We do not use Manager (it is a convenience wrapper around the
 * camera), and importing OLCesium directly both sidesteps the upstream bug and
 * leaves its dependencies out of the chunk.
 */
export type OLCesiumInstance = {
  getCesiumScene: () => any
  getCamera: () => { readFromView: () => void } | undefined
  setEnabled: (enabled: boolean) => void
  enableAutoRenderLoop: () => void
  setRefresh2DAfterCameraMoveEndOnly: (value: boolean) => void
  destroy: () => void
}

type OLCesiumModule = {
  default: new (options: {
    map: unknown
    stopOpenLayersEventsPropagation?: boolean
  }) => OLCesiumInstance
}

let cesiumPromise: Promise<CesiumNamespace> | null = null
let olcsPromise: Promise<OLCesiumModule> | null = null

/**
 * Load CesiumJS and return the global namespace.
 *
 * Memoized, so overlapping callers share one download. A failure clears the
 * memo: without that, one offline moment would leave 3D permanently broken for
 * the rest of the session even after the network came back.
 */
export const loadCesium = (
  importModule: ModuleImporter = defaultImporter,
): Promise<CesiumNamespace> => {
  if (window.Cesium) return Promise.resolve(window.Cesium)

  if (!cesiumPromise) {
    // Cesium reads CESIUM_BASE_URL while the module evaluates, to locate its
    // Workers/, Assets/ and ThirdParty/ trees. It has to be set before the
    // import runs, not after it resolves.
    window.CESIUM_BASE_URL = CESIUM_BASE_URL

    cesiumPromise = importModule(`${CESIUM_BASE_URL}index.js`)
      .then((namespace) => {
        if (!namespace || typeof namespace.Scene !== 'function') {
          throw new Error('CesiumJS loaded but did not export a usable Scene')
        }
        // ol-cesium reaches CesiumJS exclusively through this global, so the
        // module namespace has to be published under it by hand.
        window.Cesium = namespace
        return namespace
      })
      .catch((error) => {
        cesiumPromise = null
        throw error
      })
  }

  return cesiumPromise
}

/**
 * Load ol-cesium, with CesiumJS already in place.
 *
 * The dynamic import is what keeps olcs in its own async chunk; the ordering
 * matters because olcs touches `window.Cesium` as soon as it is used.
 */
export const loadOlCesium = (
  importModule: ModuleImporter = defaultImporter,
): Promise<OLCesiumModule> => {
  if (!olcsPromise) {
    olcsPromise = loadCesium(importModule)
      .then(
        () =>
          import(
            /* webpackChunkName: "olcs" */ 'olcs/OLCesium.js'
          ) as unknown as Promise<OLCesiumModule>,
      )
      .catch((error) => {
        olcsPromise = null
        throw error
      })
  }

  return olcsPromise
}

/** Test seam: drops the memoized promises. */
export const resetCesiumLoaderForTests = () => {
  cesiumPromise = null
  olcsPromise = null
}
